import { LinearPartition } from '../../../../linear-partition';
import { LoggerService } from '../../core/services/logger.service';
import { MessageImage } from '../../core/models/message-image.model';
import { Router } from '@angular/router';
import {
  Component,
  OnInit,
  HostListener,
  AfterViewInit,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { ASCII_REMOTE_KEYS } from '../../core/services/remote-control.service';
import { Store } from '@ngrx/store';
import { MessagesState } from 'src/app/state/messages.reducer';
import {
  selectAllSupporterImages,
  selectMediaImagesLoading,
  selectMediaImagesError,
} from 'src/app/state/messages.selectors';
import { Observable, Subscription } from 'rxjs';

interface LayoutImage {
  image: MessageImage;
  width: number;
  height: number;
  loaded: boolean;
}

@Component({
    selector: 'cm-media-library',
    templateUrl: './media-library.component.html',
    styleUrls: ['./media-library.component.scss'],
    standalone: false
})
export class MediaLibraryComponent implements OnInit, AfterViewInit, OnDestroy {
  showImageViewer = false;
  viewerVisible = false;
  currentImage: MessageImage;
  originalImages: MessageImage[] = [];
  layoutImages: LayoutImage[] = [];
  activeIndex = 0;
  imagesPerRow: number[] = [];

  loading$: Observable<boolean>;
  error$: Observable<string>;

  private images$: Observable<MessageImage[]>;
  private imagesSubscription: Subscription;
  private viewReady = false;

  private el: HTMLElement;

  constructor(
    private router: Router,
    private logger: LoggerService,
    private elementRef: ElementRef,
    private store$: Store<MessagesState>,
  ) {
    this.el = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.loading$ = this.store$.select(selectMediaImagesLoading);
    this.error$ = this.store$.select(selectMediaImagesError);
    this.images$ = this.store$.select(selectAllSupporterImages);
    this.imagesSubscription = this.images$.subscribe(
      (images: MessageImage[]) => {
        this.originalImages = images;
        if (this.viewReady && images.length > 0) {
          this.layoutImages = this.computeLayout(images);
        } else {
          this.layoutImages = images.map(img => ({ image: img, width: 0, height: 0, loaded: false }));
        }
        if (this.activeIndex >= images.length) {
          this.activeIndex = Math.max(0, images.length - 1);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.originalImages && this.originalImages.length > 0) {
      this.layoutImages = this.computeLayout(this.originalImages);
    }
  }

  private computeLayout(sourceImages: MessageImage[]): LayoutImage[] {
    const containerEl = this.el.querySelector('.images-grid');
    if (!containerEl) {
      return sourceImages.map(img => ({ image: img, width: 0, height: 0, loaded: false }));
    }
    const viewportWidth = containerEl.clientWidth - 12;
    const idealHeight = window.innerHeight / 2;
    const summedWidth = sourceImages.reduce((sum, img) => sum + img.aspect_ratio * idealHeight, 0);
    const rows = Math.round(summedWidth / viewportWidth);

    this.imagesPerRow = [];

    if (rows < 1) {
      this.imagesPerRow = [sourceImages.length];
      return sourceImages.map(img => ({
        image: img,
        width: Math.floor(img.aspect_ratio * idealHeight),
        height: Math.floor(idealHeight),
        loaded: false,
      }));
    }

    const weights = sourceImages.map(m => Math.floor(m.aspect_ratio * 100));
    const partition = LinearPartition.partition(weights, rows);

    const result: LayoutImage[] = [];
    let index = 0;

    partition.forEach((row) => {
      const rowImages: MessageImage[] = [];
      row.forEach(() => rowImages.push(sourceImages[index++]));
      this.imagesPerRow.push(rowImages.length);

      const summedArs = rowImages.reduce((sum, m) => sum + m.aspect_ratio, 0);
      rowImages.forEach(img => {
        result.push({
          image: img,
          width: Math.floor((viewportWidth / summedArs) * img.aspect_ratio) - 4,
          height: Math.floor(viewportWidth / summedArs) - 4,
          loaded: false,
        });
      });
    });

    return result;
  }

  ngOnDestroy(): void {
    this.imagesSubscription?.unsubscribe();
  }

  onImageLoaded(index: number): void {
    if (this.layoutImages[index]) {
      this.layoutImages[index].loaded = true;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown($event: KeyboardEvent): boolean {
    if (this.showImageViewer) return false;

    switch ($event.keyCode) {
      case ASCII_REMOTE_KEYS.back:
        this.goToMenu();
        break;
      case ASCII_REMOTE_KEYS.right:
        this.navigate(1);
        break;
      case ASCII_REMOTE_KEYS.left:
        this.navigate(-1);
        break;
      case ASCII_REMOTE_KEYS.down:
        this.navigateVertical(1);
        break;
      case ASCII_REMOTE_KEYS.up:
        this.navigateVertical(-1);
        break;
      case ASCII_REMOTE_KEYS.ok:
        this.openViewer();
        break;
    }

    return false;
  }

  navigate(delta: number): void {
    const newIndex = this.activeIndex + delta;
    if (newIndex >= 0 && newIndex < this.layoutImages.length) {
      this.activeIndex = newIndex;
      this.scrollToActive();
    }
  }

  navigateVertical(direction: number): void {
    if (this.imagesPerRow.length === 0) return;

    let currentRow = 0;
    let posInRow = this.activeIndex;
    let accumulated = 0;

    for (let i = 0; i < this.imagesPerRow.length; i++) {
      if (accumulated + this.imagesPerRow[i] > this.activeIndex) {
        currentRow = i;
        posInRow = this.activeIndex - accumulated;
        break;
      }
      accumulated += this.imagesPerRow[i];
    }

    const targetRow = currentRow + direction;
    if (targetRow < 0 || targetRow >= this.imagesPerRow.length) return;

    let targetStart = 0;
    for (let i = 0; i < targetRow; i++) {
      targetStart += this.imagesPerRow[i];
    }
    const targetRowLength = this.imagesPerRow[targetRow];
    const targetPosInRow = Math.min(posInRow, targetRowLength - 1);

    this.activeIndex = targetStart + targetPosInRow;
    this.scrollToActive();
  }

  private scrollToActive(): void {
    setTimeout(() => {
      const photoEl = this.el.querySelector(`[data-photo-index="${this.activeIndex}"]`);
      if (photoEl) {
        photoEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    });
  }

  openViewer(): void {
    if (this.activeIndex < 0 || this.activeIndex >= this.layoutImages.length) return;

    this.currentImage = this.layoutImages[this.activeIndex].image;
    this.showImageViewer = true;
    setTimeout(() => this.viewerVisible = true, 16);
  }

  onBackPressed($event: MessageImage): void {
    this.viewerVisible = false;
    setTimeout(() => {
      this.showImageViewer = false;

      // Find the index of the image we were viewing (may have changed if user navigated in viewer)
      if ($event) {
        const returnIndex = this.layoutImages.findIndex(li => li.image.id === $event.id);
        if (returnIndex !== -1) {
          this.activeIndex = returnIndex;
        }
      }

      this.scrollToActive();
    }, 300);
  }

  goToMenu(): void {
    this.router.navigate(['dashboard/menu']);
  }
}
